"use client";

import React, { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/Button";

type Organization = {
  id: string;
  name: string;
  role: string;
};

export default function IdentityGatewayPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  
  // Steps: 
  // 1: Enter Email
  // 2: Multi-Org Selection (Scenario C)
  // 3: Verify OTP (Scenarios A, B, C)
  // 4: Zero Orgs / Request Access (Scenario D)
  // 5: Create New Org Name (Scenario A part 1)
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Identity Context
  const [identityExists, setIdentityExists] = useState(false);
  const [accessibleOrgs, setAccessibleOrgs] = useState<Organization[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [selectedTenantName, setSelectedTenantName] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [newOrgName, setNewOrgName] = useState("");

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch("/api/identity/lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to lookup identity.");

      setIdentityExists(data.identityExists);
      setAccessibleOrgs(data.accessibleOrgs || []);

      if (data.accessibleOrgs.length === 1) {
        // Scenario B: Exactly 1 org
        setSelectedTenantId(data.accessibleOrgs[0].id);
        setSelectedTenantName(data.accessibleOrgs[0].name);
        setSelectedRole(data.accessibleOrgs[0].role);
        await issueOtp(email.trim());
      } else if (data.accessibleOrgs.length > 1) {
        // Scenario C: Multiple orgs
        setStep(2);
      } else {
        // No orgs
        if (!data.identityExists) {
          // Scenario A: Identity not found, create org
          setStep(5);
        } else {
          // Scenario D: Identity exists, no orgs
          setStep(4);
        }
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      setError(err.name === "AbortError" ? "Connection timed out. Please check your internet or retry." : (err.message || "Failed to lookup identity."));
    } finally {
      setLoading(false);
    }
  };

  const handleOrgSelection = async (org: Organization) => {
    setSelectedTenantId(org.id);
    setSelectedTenantName(org.name);
    setSelectedRole(org.role);
    await issueOtp(email.trim());
  };

  const issueOtp = async (targetEmail: string) => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetch("/api/auth/otp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: targetEmail }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to issue OTP.");
      setStep(3); // Go to OTP verification step
    } catch (err: any) {
      clearTimeout(timeoutId);
      setError(err.name === "AbortError" ? "OTP service timed out. Please retry." : (err.message || "Failed to issue OTP."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setLoading(true);
    setError(null);

    try {
      let finalTenantId = selectedTenantId;

      // Scenario A: Create Org during verification if they are a new user creating an org
      if (!identityExists && newOrgName.trim()) {
        const res = await fetch("/api/admin/tenants/initialize", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim(), orgName: newOrgName.trim() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to initialize organization.");
        finalTenantId = data.tenantId;
      }

      if (!finalTenantId) {
        throw new Error("No organization context selected.");
      }

      const res = await signIn("supabase-otp", {
        email: email.trim(),
        code: otp.trim(),
        tenantId: finalTenantId,
        redirect: false,
      });

      if (res?.error) throw new Error(res.error);
      router.push("/dashboard");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-[2rem] p-10 shadow-[0px_40px_100px_rgba(0,40,115,0.08)] border border-slate-100">
        <div className="text-center mb-10">
          <div className="h-16 w-16 bg-[#002873] rounded-2xl mx-auto flex items-center justify-center mb-6 shadow-xl shadow-blue-900/20">
            <span className="material-symbols-outlined text-white text-3xl">
              {step === 3 ? "lock" : step === 2 ? "domain" : "fingerprint"}
            </span>
          </div>
          <h1 className="text-3xl font-black text-[#002873] tracking-tighter">
            {step === 1 ? "Identity Gateway" :
             step === 2 ? "Select Organization" :
             step === 3 ? "Verify Identity" :
             step === 4 ? "No Organization" :
             "Create Organization"}
          </h1>
        </div>

        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-2xl text-xs font-bold mb-6 border border-red-100">
            {error}
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Identity Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                placeholder="name@company.com"
              />
            </div>
            <Button type="submit" variant="primary" className="w-full !bg-[#002873] !text-white" disabled={loading}>
              {loading ? "Authenticating..." : "Continue"}
            </Button>
          </form>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {accessibleOrgs.map((org) => (
              <div 
                key={org.id} 
                onClick={() => handleOrgSelection(org)}
                className="p-4 border border-slate-200 rounded-xl cursor-pointer hover:border-cyan-500 hover:bg-cyan-50 transition-all flex justify-between items-center"
              >
                <div>
                  <h3 className="font-bold text-slate-800">{org.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">Role: {org.role}</p>
                </div>
                <span className="material-symbols-outlined text-slate-400">chevron_right</span>
              </div>
            ))}
          </div>
        )}

        {step === 3 && (
          <form onSubmit={handleVerifyOtp} className="space-y-6">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-center">
              <p className="text-xs text-slate-500 mb-1">You are signing into</p>
              <p className="font-bold text-slate-800">{selectedTenantName || newOrgName}</p>
              {selectedRole && <p className="text-xs text-slate-500 mt-1">Role: {selectedRole}</p>}
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 text-center">Security Code</label>
              <div className="flex items-center justify-center gap-2">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <React.Fragment key={index}>
                    <input
                      id={`otp-${index}`}
                      type="text"
                      maxLength={1}
                      value={otp[index] || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!/^[0-9]*$/.test(val)) return;
                        const newOtp = otp.split('');
                        newOtp[index] = val;
                        setOtp(newOtp.join(''));
                        if (val && index < 5) {
                          document.getElementById(`otp-${index + 1}`)?.focus();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !otp[index] && index > 0) {
                          document.getElementById(`otp-${index - 1}`)?.focus();
                        }
                      }}
                      className="w-12 h-14 text-center text-2xl font-mono rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white shadow-sm"
                      placeholder="0"
                    />
                    {index === 2 && <div className="w-4" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <Button type="submit" variant="primary" className="w-full !bg-[#002873] !text-white" disabled={loading}>
              {loading ? "Verifying..." : "Verify & Launch"}
            </Button>
          </form>
        )}

        {step === 4 && (
          <div className="space-y-4 text-center">
            <p className="text-slate-600 mb-6">You currently do not belong to any organization.</p>
            <Button variant="primary" className="w-full !bg-[#002873] !text-white" onClick={() => setStep(5)}>
              Create New Organization
            </Button>
            <Button variant="outline" className="w-full" onClick={() => router.push("/auth/requests")}>
              Request Organization Access
            </Button>
          </div>
        )}

        {step === 5 && (
          <form onSubmit={(e) => { e.preventDefault(); issueOtp(email.trim()); }} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Organization Name</label>
              <input
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Enter your organization name"
              />
            </div>
            <Button type="submit" variant="primary" className="w-full !bg-[#002873] !text-white" disabled={loading || !newOrgName.trim()}>
              Continue
            </Button>
          </form>
        )}

      </div>
    </div>
  );
}
